import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type {
  CreateLeaveEntitlementDTO,
  LeaveEntitlementListItem,
  UpdateLeaveEntitlementDTO,
} from "@/modules/hr/leave/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  useCreateLeaveEntitlementMutation,
  useLeaveEntitlementsQuery,
  useLeaveTypesQuery,
  useUpdateLeaveEntitlementMutation,
} from "../api/queries";
import {
  LeaveEntitlementByEmployeeTour,
  useLeaveEntitlementByEmployeeTour,
} from "../tour/LeaveEntitlementByEmployeeTour";
import { LeaveEntitlementAddTypeDialog } from "../ui/LeaveEntitlementAddTypeDialog";
import { LeaveEntitlementByEmployeeCard } from "../ui/LeaveEntitlementByEmployeeCard";
import { LeaveEntitlementEditDialog } from "../ui/LeaveEntitlementEditDialog";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: 6 },
  (_, i) => CURRENT_YEAR - 2 + i,
).map((y) => ({ value: String(y), label: String(y) }));

export function LeaveEntitlementByEmployeePage() {
  const { employeeId } = useParams({ strict: false });
  const { has } = usePermissions();
  const canCreateEntitlement = has(Permissions.hr_leave.entitlements_create);
  const search = useSearch({
    from: "/app/hr/leave/entitlements/employee/$employeeId",
  }) as { year?: number };
  const year = search.year ?? CURRENT_YEAR;
  const nav = useNavigate();
  const tour = useLeaveEntitlementByEmployeeTour();

  const query = useMemo(
    () => ({
      offset: 0,
      limit: 50,
      year,
      employeeId: employeeId ?? "",
    }),
    [year, employeeId],
  );

  const listQuery = useLeaveEntitlementsQuery(query);
  const entitlements = listQuery.data?.data ?? [];
  const employeeName = entitlements[0]?.employeeName ?? "—";
  const employeeEmail = entitlements[0]?.employeeEmail ?? "";

  const typesQuery = useLeaveTypesQuery({
    isActive: true,
    limit: 100,
  });
  const activeLeaveTypes = typesQuery.data?.data ?? [];

  const missingLeaveTypes = useMemo(() => {
    const presentIds = new Set(entitlements.map((e) => e.leaveTypeId));
    return activeLeaveTypes.filter((t) => !presentIds.has(t.id));
  }, [entitlements, activeLeaveTypes]);

  const [editRow, setEditRow] = useState<LeaveEntitlementListItem | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const updateMutation = useUpdateLeaveEntitlementMutation();
  const createMutation = useCreateLeaveEntitlementMutation();

  const handleBack = () => {
    nav({
      to: "/app/hr/leave/entitlements",
      search: {
        year,
        offset: 0,
        limit: 20,
        sort: undefined,
        leaveTypeId: undefined,
        search: undefined,
      },
    });
  };

  const handleYearChange = (value: string) => {
    const newYear = Number(value);
    if (Number.isNaN(newYear)) return;
    nav({
      to: "/app/hr/leave/entitlements/employee/$employeeId",
      params: { employeeId: employeeId ?? "" },
      search: { year: newYear },
    });
  };

  const handleEdit = (row: LeaveEntitlementListItem) => {
    setEditRow(row);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = (dto: UpdateLeaveEntitlementDTO) => {
    if (!editRow) return;
    updateMutation.mutate(
      { id: editRow.id, dto },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກການແກ້ໄຂສິດລາສຳເລັດ");
          setEditDialogOpen(false);
          setEditRow(null);
        },
        onError: (err) => {
          toast.error(err?.message ?? "ເກີດຂໍ້ຜິດພາດ");
        },
      },
    );
  };

  const handleAddSubmit = (dto: CreateLeaveEntitlementDTO) => {
    createMutation.mutate(dto, {
      onSuccess: () => {
        toast.success("ເພີ່ມສິດລາສຳເລັດ");
        setAddDialogOpen(false);
      },
      onError: (err) => {
        toast.error(err?.message ?? "ເກີດຂໍ້ຜິດພາດ");
      },
    });
  };

  const showAddTypeButton =
    canCreateEntitlement &&
    !!employeeId &&
    missingLeaveTypes.length > 0 &&
    !listQuery.isLoading &&
    !typesQuery.isLoading;

  const employeeDisplayNameForAdd =
    entitlements.length > 0
      ? `${employeeName}${employeeEmail ? ` (${employeeEmail})` : ""}`
      : (employeeId ?? "—");

  return (
    <>
      <Header />
      <Main>
        <LeaveEntitlementByEmployeeTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mx-auto max-w-4xl space-y-6">
          <div
            className="flex items-center gap-4"
            data-tourid="leave-entitlement-by-employee-back"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="gap-2"
              aria-label="ກັບຄືນສິດລາຕໍ່ປີ"
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນສິດລາຕໍ່ປີ
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
              title="ເລີ່ມທົດສອບການນຳທາງ"
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-semibold text-2xl">ລາຍລະອຽດສິດລາຕໍ່ປະເພດ</h1>
              <p className="text-muted-foreground text-sm">
                {employeeName}
                {employeeEmail ? ` (${employeeEmail})` : ""}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {showAddTypeButton ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setAddDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  ເພີ່ມປະເພດລາ
                </Button>
              ) : null}
              <label
                htmlFor="entitlement-year"
                className="text-muted-foreground text-sm"
              >
                ປີ
              </label>
              <SimpleSelect
                id="entitlement-year"
                options={YEAR_OPTIONS}
                value={String(year)}
                onValueChange={handleYearChange}
                placeholder="ເລືອກປີ"
                className="w-28"
              />
            </div>
          </div>

          <div data-tourid="leave-entitlement-by-employee-cards">
            <LeaveEntitlementByEmployeeCard
              entitlements={entitlements}
              year={year}
              isLoading={listQuery.isLoading}
              onEdit={handleEdit}
            />
          </div>
        </div>
      </Main>

      <LeaveEntitlementEditDialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setEditRow(null);
        }}
        entitlement={editRow}
        onSubmit={handleEditSubmit}
        isSubmitting={updateMutation.isPending}
      />

      {employeeId ? (
        <LeaveEntitlementAddTypeDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          employeeId={employeeId}
          year={year}
          employeeDisplayName={employeeDisplayNameForAdd}
          missingLeaveTypes={missingLeaveTypes}
          onSubmit={handleAddSubmit}
          isSubmitting={createMutation.isPending}
        />
      ) : null}
    </>
  );
}
